import type { FinancialStatementInventoryType } from '@api/api/inventory-schema';
import { TabsType } from '@terminal/modules/tab-content/types';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { t } from 'i18next';

export const STATEMENT_TO_QUERY_PARAM: Record<
  FinancialStatementInventoryType,
  string
> = {
  income: 'incomeStatement',
  cashflow: 'cashFlow',
  balancesheet: 'balanceSheet',
};

export const QUERY_PARAM_TO_STATEMENT: Record<
  string,
  FinancialStatementInventoryType
> = {
  incomeStatement: 'income',
  cashFlow: 'cashflow',
  balanceSheet: 'balancesheet',
};

const STATEMENT_I18N_KEYS: Record<FinancialStatementInventoryType, string> = {
  income: 'chat:financialStatement.income',
  cashflow: 'chat:financialStatement.cashflow',
  balancesheet: 'chat:financialStatement.balancesheet',
};

export const getStatementDisplayLabel = (
  statement: FinancialStatementInventoryType
): string => t(STATEMENT_I18N_KEYS[statement]);

export interface FinancialStatementItem {
  entityTag: string;
  statement?: FinancialStatementInventoryType;
}

export const getAssetOverviewFinancialsUrl = (
  entityTag: string,
  baseUrl: string,
  statement?: FinancialStatementInventoryType
): string => {
  const params = new URLSearchParams();
  params.set(queryKeys.assetOverviewAsset, entityTag);
  params.set(queryKeys.assetOverviewTab, TabsType.Financials);
  if (statement) {
    params.set(
      queryKeys.activeFinancialsStatement,
      STATEMENT_TO_QUERY_PARAM[statement]
    );
  }
  return `${baseUrl}${appPaths.assetOverview}?${params.toString()}`;
};

export interface FinancialStatementRawItem {
  tag: string;
  statement?: FinancialStatementInventoryType;
}

export const normalizeFinancialStatementItems = (
  rawItems: FinancialStatementRawItem[]
): FinancialStatementItem[] =>
  rawItems.map(item => ({
    entityTag: item.tag,
    statement: item.statement,
  }));

export const getFinancialStatementFromQueryParam = (
  value: string | undefined
): FinancialStatementInventoryType | undefined =>
  value ? QUERY_PARAM_TO_STATEMENT[value] : undefined;

export const findFinancialStatementItemForQueryParam = (
  items: FinancialStatementItem[],
  value: string | undefined,
  entityTag?: string
): FinancialStatementItem | undefined => {
  if (!value) {
    return undefined;
  }

  const statement = getFinancialStatementFromQueryParam(value);
  const scopedItems = entityTag
    ? items.filter(item => item.entityTag === entityTag)
    : items;
  return (
    scopedItems.find(item => item.statement === statement) ??
    scopedItems.find(item => !!item.entityTag) ??
    items.find(item => item.statement === statement) ??
    items.find(item => !!item.entityTag) ??
    items[0]
  );
};
